import React, { useEffect, useState } from 'react'
import {
  collection, query, where, onSnapshot, addDoc, updateDoc, doc, deleteDoc
} from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'

interface Project {
  id: string
  name: string
  code?: string
  orgId: string
  status?: string
  location?: { latitude: number; longitude: number; address?: string }
  musterRadiusMeters?: number
  description?: string
}

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-700',
}

export default function ProjectsPage() {
  const { orgId, isAdmin, setSelectedProjectId, selectedProjectId } = useAuth()
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<Project | null>(null)
  const [saving, setSaving] = useState(false)

  const [form, setForm] = useState({
    name: '',
    code: '',
    status: 'active',
    description: '',
    latitude: '',
    longitude: '',
    address: '',
    musterRadiusMeters: '1609',
  })

  useEffect(() => {
    if (!orgId) return
    const q = query(collection(db, 'projects'), where('orgId', '==', orgId))
    const unsub = onSnapshot(q, snap => {
      setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() } as Project)))
      setLoading(false)
    })
    return () => unsub()
  }, [orgId])

  const resetForm = () => {
    setForm({
      name: '',
      code: '',
      status: 'active',
      description: '',
      latitude: '',
      longitude: '',
      address: '',
      musterRadiusMeters: '1609',
    })
  }

  const startEdit = (p: Project) => {
    setEditing(p)
    setForm({
      name: p.name || '',
      code: p.code || '',
      status: p.status || 'active',
      description: p.description || '',
      latitude: p.location?.latitude != null ? String(p.location.latitude) : '',
      longitude: p.location?.longitude != null ? String(p.location.longitude) : '',
      address: p.location?.address || '',
      musterRadiusMeters: String(p.musterRadiusMeters || 1609),
    })
    setShowCreate(true)
  }

  const startCreate = () => {
    setEditing(null)
    resetForm()
    setShowCreate(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgId) return
    setSaving(true)
    try {
      const payload: any = {
        name: form.name.trim(),
        code: form.code.trim(),
        status: form.status,
        description: form.description.trim(),
        orgId,
        updatedAt: new Date().toISOString(),
      }
      const lat = parseFloat(form.latitude)
      const lng = parseFloat(form.longitude)
      if (!isNaN(lat) && !isNaN(lng)) {
        payload.location = {
          latitude: lat,
          longitude: lng,
          address: form.address.trim(),
        }
      }
      const radius = parseFloat(form.musterRadiusMeters)
      payload.musterRadiusMeters = isNaN(radius) || radius <= 0 ? 1609 : radius

      if (editing) {
        await updateDoc(doc(db, 'projects', editing.id), payload)
      } else {
        payload.createdAt = new Date().toISOString()
        await addDoc(collection(db, 'projects'), payload)
      }
      setShowCreate(false)
      resetForm()
      setEditing(null)
    } catch (err: any) {
      alert('Failed to save project: ' + (err.message || 'unknown error'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (p: Project) => {
    if (!window.confirm(`Delete project "${p.name}"? This cannot be undone.`)) return
    try {
      await deleteDoc(doc(db, 'projects', p.id))
      if (selectedProjectId === p.id) setSelectedProjectId('')
    } catch (err: any) {
      alert('Failed to delete project: ' + (err.message || 'unknown error'))
    }
  }

  if (!isAdmin()) {
    return (
      <AppLayout title="Projects">
        <p className="text-gray-500 text-sm">You do not have permission to manage projects.</p>
      </AppLayout>
    )
  }

  return (
    <AppLayout title="Projects">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-gray-500">Create and manage job sites. Set each project's center pin and muster radius.</p>
        <button onClick={startCreate}
          className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700">
          + New Project
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Name</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Code</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Status</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Center</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600">Muster Radius</th>
              <th className="text-left px-5 py-3 font-medium text-gray-600"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-gray-400">Loading...</td></tr>
            ) : projects.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-gray-400">No projects yet.</td></tr>
            ) : projects.map(p => (
              <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-5 py-3 font-medium text-gray-900">
                  <div className="flex items-center gap-2">
                    {p.name}
                    {selectedProjectId === p.id && (
                      <span className="text-[10px] bg-accent text-white px-1.5 py-0.5 rounded">Selected</span>
                    )}
                  </div>
                </td>
                <td className="px-5 py-3 text-gray-600">{p.code || '—'}</td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[p.status || 'active'] || STATUS_COLORS.active}`}>
                    {p.status || 'active'}
                  </span>
                </td>
                <td className="px-5 py-3 text-gray-600 text-xs">
                  {p.location ? `${p.location.latitude.toFixed(4)}, ${p.location.longitude.toFixed(4)}` : '—'}
                </td>
                <td className="px-5 py-3 text-gray-600 text-xs">
                  {p.musterRadiusMeters ? `${Math.round(p.musterRadiusMeters / 1609 * 10) / 10} mi` : '1.0 mi'}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-3">
                    <button onClick={() => { setSelectedProjectId(p.id); }}
                      className={`text-xs font-medium ${selectedProjectId === p.id ? 'text-accent' : 'text-blue-600 hover:underline'}`}>
                      {selectedProjectId === p.id ? 'Current' : 'Select'}
                    </button>
                    <button onClick={() => startEdit(p)} className="text-xs text-gray-600 hover:text-gray-900 font-medium">Edit</button>
                    <button onClick={() => handleDelete(p)} className="text-xs text-red-600 hover:underline font-medium">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">{editing ? 'Edit Project' : 'New Project'}</h2>
              <button onClick={() => { setShowCreate(false); resetForm(); setEditing(null); }} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input type="text" required value={form.name}
                    onChange={e => setForm({ ...form, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input type="text" value={form.code}
                    onChange={e => setForm({ ...form, code: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                  <input type="number" step="any" value={form.latitude}
                    onChange={e => setForm({ ...form, latitude: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                  <input type="number" step="any" value={form.longitude}
                    onChange={e => setForm({ ...form, longitude: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address / Site Name</label>
                <input type="text" value={form.address}
                  onChange={e => setForm({ ...form, address: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Muster Radius (meters)</label>
                <div className="flex items-center gap-3">
                  <input type="number" min={100} step={100} value={form.musterRadiusMeters}
                    onChange={e => setForm({ ...form, musterRadiusMeters: e.target.value })}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm" />
                  <span className="text-sm text-gray-600 w-24 text-right">
                    {Math.round(Number(form.musterRadiusMeters || 1609) / 1609 * 10) / 10} mi
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status}
                  onChange={e => setForm({ ...form, status: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm bg-white">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreate(false); resetForm(); setEditing(null); }}
                  className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg font-medium hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving}
                  className="flex-1 bg-accent text-white py-2.5 rounded-lg font-medium hover:bg-amber-700 disabled:bg-amber-300">
                  {saving ? 'Saving...' : (editing ? 'Save Changes' : 'Create Project')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AppLayout>
  )
}
